import { PipelinesLoadingFallback } from '@/features/pipelines/components/PipelinesLoadingFallback';
import { PipelinesTablePanel } from '@/features/pipelines/components/PipelinesTablePanel';
import { usePipelinesPageViewModel } from '@/features/pipelines/hooks';
import { StackedMetricsStatusChartSection } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import { EntityDailyHistoryPanel } from '@/features/shared/components/EntityDailyHistoryPanel';
import { DelayedSkeletonGate } from '@/ui/composites';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import type { ReactElement } from 'react';

export const PipelinesPage = (): ReactElement => {
  const {
    dailyHistoryDateRangeLabel,
    dailyHistoryLoading,
    dailyHistoryNoRowsText,
    dailyHistoryPage,
    dailyHistoryPageSize,
    dailyHistoryRows,
    dailyHistorySortDirection,
    dailyHistorySortKey,
    dailyHistoryTotalCount,
    metricSectionProps,
    setDailyHistoryPage,
    setDailyHistoryPageSize,
    setDailyHistorySortDirection,
    setDailyHistorySortKey,
    showPageSkeleton,
    tablePanelProps,
  } = usePipelinesPageViewModel();

  return (
    <DelayedSkeletonGate show={showPageSkeleton} fallback={<PipelinesLoadingFallback />}>
      <ListPageTemplate
        title={
          <Text role="sectionTitle" component="h1">
            Pipelines
          </Text>
        }
        content={
          <Stack gap="2xl">
            <StackedMetricsStatusChartSection {...metricSectionProps} />
            <EntityDailyHistoryPanel
              title="Build History"
              dateRangeLabel={dailyHistoryDateRangeLabel}
              entityLabel="Pipeline"
              activityLabel="builds"
              rows={dailyHistoryRows}
              loading={dailyHistoryLoading}
              noRowsText={dailyHistoryNoRowsText}
              sortKey={dailyHistorySortKey}
              sortDirection={dailyHistorySortDirection}
              sortOptions={[{ key: 'name', label: 'Pipeline' }]}
              onSortChange={(nextSortKey, nextSortDirection) => {
                setDailyHistorySortKey(nextSortKey);
                setDailyHistorySortDirection(nextSortDirection);
                setDailyHistoryPage(0);
              }}
              page={dailyHistoryPage}
              pageSize={dailyHistoryPageSize}
              totalCount={dailyHistoryTotalCount}
              onPageChange={setDailyHistoryPage}
              onPageSizeChange={(nextPageSize) => {
                setDailyHistoryPageSize(nextPageSize);
                setDailyHistoryPage(0);
              }}
            />
            <PipelinesTablePanel {...tablePanelProps} />
          </Stack>
        }
      />
    </DelayedSkeletonGate>
  );
};
